import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, ArrowRight, CalendarDays, Search, TriangleAlert } from 'lucide-react'
import { supabase } from '../supabase'
import { useUser } from '../UserContext'
import { buildAlerts } from '../lib/alerts'

function formatDate(value) {
  if (!value) return 'No date'
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function Alerts() {
  const navigate = useNavigate()
  const { orgId } = useUser()
  const [deliveries, setDeliveries] = useState([])
  const [deals, setDeals] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [type, setType] = useState('all')

  useEffect(() => {
    if (!orgId) return

    async function fetchAlerts() {
      setLoading(true)
      setError(null)

      const [deliveryResult, dealResult, clientResult] = await Promise.all([
        supabase
          .from('secure_deliveries')
          .select('id, name, status, delivery_date, custom_fields, org_id, rights(name, deal_id, deals(id, name, clients(name)))')
          .eq('org_id', orgId),
        supabase
          .from('secure_deals')
          .select('id, client_id, name, status, end_date, org_id')
          .eq('org_id', orgId),
        supabase
          .from('clients')
          .select('id, name, org_id')
          .eq('org_id', orgId)
      ])

      const fetchError = deliveryResult.error || dealResult.error || clientResult.error
      if (fetchError) {
        setError(`Failed to load alerts: ${fetchError.message}`)
      } else {
        setDeliveries(deliveryResult.data || [])
        setDeals(dealResult.data || [])
        setClients(clientResult.data || [])
      }

      setLoading(false)
    }

    fetchAlerts()
  }, [orgId])

  const alerts = useMemo(() => {
    const clientNames = new Map(clients.map(client => [client.id, client.name]))
    const enrichedDeals = deals.map(deal => ({ ...deal, client_name: clientNames.get(deal.client_id) }))
    return buildAlerts({ deliveries, deals: enrichedDeals })
  }, [clients, deals, deliveries])

  const filteredAlerts = alerts.filter(alert => {
    const query = search.trim().toLowerCase()
    const matchesType = type === 'all' || alert.type === type
    const matchesSearch = !query || [alert.title, alert.subtitle, alert.clientName]
      .filter(Boolean)
      .some(value => value.toLowerCase().includes(query))
    return matchesType && matchesSearch
  })

  const overdueCount = alerts.filter(alert => alert.type === 'delivery').length
  const expiringCount = alerts.filter(alert => alert.type === 'deal').length

  return (
    <div className="hv-page alerts-page">
      <style>{`
        .alerts-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin-bottom: 20px; }
        .alerts-stat { display: flex; align-items: center; gap: 14px; padding: 18px 20px; }
        .alerts-stat-icon { display: inline-flex; width: 44px; height: 44px; flex: 0 0 44px; align-items: center; justify-content: center; border-radius: 50%; color: #4F46E5; background: #EEF2FF; }
        .alerts-stat-icon.red { color: #DC2626; background: #FEF2F2; }
        .alerts-stat-icon.amber { color: #D97706; background: #FFF7E6; }
        .alerts-stat-label { color: var(--hv-text-muted); font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .alerts-stat-value { color: var(--hv-text-primary); font-size: 25px; font-weight: 800; margin-top: 7px; }
        .alerts-toolbar { display: grid; grid-template-columns: minmax(240px, 1fr) 210px; gap: 12px; margin-bottom: 18px; }
        .alerts-list { overflow: hidden; }
        .alerts-row { width: 100%; border: 0; border-bottom: 1px solid var(--hv-border); background: #fff; padding: 17px 20px; display: grid; grid-template-columns: 38px minmax(0, 1fr) auto auto 24px; gap: 14px; align-items: center; text-align: left; cursor: pointer; color: inherit; }
        .alerts-row:last-child { border-bottom: 0; }
        .alerts-row:hover { background: var(--hv-surface-hover); }
        .alerts-row:disabled { cursor: default; opacity: .7; }
        .alerts-icon { width: 36px; height: 36px; border-radius: 8px; display: flex; align-items: center; justify-content: center; }
        .alerts-title { font-size: 13.5px; font-weight: 750; color: var(--hv-text-primary); }
        .alerts-meta { font-size: 12px; color: var(--hv-text-muted); margin-top: 4px; }
        .alerts-date { display: flex; align-items: center; gap: 6px; color: var(--hv-text-secondary); font-size: 12px; white-space: nowrap; }
        @media (max-width: 760px) {
          .alerts-summary { grid-template-columns: 1fr; }
          .alerts-toolbar { grid-template-columns: 1fr; }
          .alerts-row { grid-template-columns: 38px minmax(0, 1fr) 20px; }
          .alerts-date, .alerts-row > .hv-badge { display: none; }
        }
      `}</style>

      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Alerts</h1>
          <p className="hv-page-subtitle">Review overdue deliveries and deals approaching expiry</p>
        </div>
      </div>

      <div className="alerts-summary">
        <div className="hv-card alerts-stat">
          <span className="alerts-stat-icon"><AlertCircle size={21} /></span><div><div className="alerts-stat-label">Open Alerts</div><div className="alerts-stat-value">{alerts.length}</div></div>
        </div>
        <div className="hv-card alerts-stat">
          <span className="alerts-stat-icon red"><TriangleAlert size={21} /></span><div><div className="alerts-stat-label">Overdue Deliveries</div><div className="alerts-stat-value" style={{ color: overdueCount ? 'var(--hv-red)' : undefined }}>{overdueCount}</div></div>
        </div>
        <div className="hv-card alerts-stat">
          <span className="alerts-stat-icon amber"><CalendarDays size={21} /></span><div><div className="alerts-stat-label">Expiring Deals</div><div className="alerts-stat-value" style={{ color: expiringCount ? 'var(--hv-amber)' : undefined }}>{expiringCount}</div></div>
        </div>
      </div>

      <div className="alerts-toolbar">
        <div className="hv-search-wrap" style={{ margin: 0 }}>
          <Search className="hv-search-icon" size={15} />
          <input className="hv-search" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search alerts by delivery, deal or client..." />
        </div>
        <select className="hv-select" value={type} onChange={event => setType(event.target.value)}>
          <option value="all">All alert types</option>
          <option value="delivery">Overdue deliveries</option>
          <option value="deal">Expiring deals</option>
        </select>
      </div>

      {error && <div style={{ color: 'var(--hv-red-text)', background: 'var(--hv-red-bg)', border: '1px solid #FCA5A5', borderRadius: 8, padding: '14px 18px' }}>{error}</div>}

      {loading ? (
        <div style={{ color: 'var(--hv-text-muted)', padding: '36px 0' }}>Loading alerts...</div>
      ) : filteredAlerts.length === 0 ? (
        <div className="hv-table-wrap">
          <div className="hv-empty">
            <div className="hv-empty-title">{alerts.length ? 'No alerts match your filters' : 'No open alerts'}</div>
            <div className="hv-empty-text">{alerts.length ? 'Try changing your search or alert type.' : 'Overdue deliveries and expiring deals will appear here.'}</div>
          </div>
        </div>
      ) : (
        <div className="hv-table-wrap alerts-list">
          <div className="hv-table-count">{filteredAlerts.length} {filteredAlerts.length === 1 ? 'alert' : 'alerts'}</div>
          {filteredAlerts.map(alert => {
            const critical = alert.severity === 'critical'
            return (
              <button key={alert.id} className="alerts-row" disabled={!alert.path} onClick={() => alert.path && navigate(alert.path)}>
                <span className="alerts-icon" style={{ background: critical ? 'var(--hv-red-bg)' : 'var(--hv-amber-bg)', color: critical ? 'var(--hv-red)' : 'var(--hv-amber)' }}>
                  {critical ? <AlertCircle size={18} /> : <TriangleAlert size={18} />}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span className="alerts-title">{alert.title}</span>
                  <span className="alerts-meta" style={{ display: 'block' }}>{alert.subtitle}{alert.clientName && alert.clientName !== alert.subtitle ? ` · ${alert.clientName}` : ''}</span>
                </span>
                <span className={`hv-badge ${critical ? 'hv-badge-red' : 'hv-badge-amber'}`}>{critical ? 'Overdue' : 'Expiring'}</span>
                <span className="alerts-date"><CalendarDays size={14} />{formatDate(alert.dueDate)} · {alert.detail}</span>
                <ArrowRight size={16} color="var(--hv-text-muted)" />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
